// ---------------------------------------------------------------------------
// Block Definition: chart
// ---------------------------------------------------------------------------

import { BlockRegistry } from "../index.js";

const EVIDENCE_DESC = "Verbatim sentence(s) from the SOURCE that justify this value — quote exactly, do not paraphrase.";

function nullable(type, description, extra = {}) {
  const t = Array.isArray(type) ? type : [type, "null"];
  return { type: t, ...(description ? { description } : {}), ...extra };
}

const barKind = {
  name: "bar", label: "Bar Chart",
  extractionPhases: ["structured"],
  isBlockLevelTarget: false, skipFromPinSchema: true, isCodeDerived: false,
  buildDataSchema(b,o) { return { type: "object", additionalProperties: true }; },
  buildShapeDescription(b) {
    const sks = (b.series || []).map(s => s.key).filter(Boolean);
    return `{ "x_labels": ["FY22","FY23","FY24","FY25"], "series": { ${sks.map(k => `"${k}": [<number>,...]`).join(", ")} }, "net_profit_yoy": [<number>,...] }`;
  },
};

const lineKind = {
  name: "line", label: "Line Chart",
  extractionPhases: ["structured"],
  isBlockLevelTarget: false, skipFromPinSchema: true, isCodeDerived: false,
  buildDataSchema(b,o) { return { type: "object", additionalProperties: true }; },
  buildShapeDescription(b) {
    const sks = (b.series || []).map(s => s.key).filter(Boolean);
    return `{ "x_labels": ["FY22","FY23","FY24","FY25"], "series": { ${sks.map(k => `"${k}": [<number>,...]`).join(", ")} } }`;
  },
};

const heatmapKind = {
  name: "heatmap", label: "Heatmap",
  extractionPhases(b) { return b.count_field ? ["deterministic"] : ["llm"]; },
  isBlockLevelTarget: false, skipFromPinSchema: false,
  isCodeDerived(b) { return !!b.count_field; },
  buildDataSchema(b,o) { return { type: "object", additionalProperties: true }; },
  buildShapeDescription(b) { return `{ "cells": [{ "label": "<string>", "value": <number> }, ...] }`; },
};

const progressKind = {
  name: "progress", label: "Progress Bar",
  extractionPhases: ["llm"],
  isBlockLevelTarget: true, skipFromPinSchema: false, isCodeDerived: false,
  buildDataSchema(b,o) {
    const lbl = b.label ? `${b.label} — ` : "";
    const props = { value: nullable("number", lbl + (b.value?.prompt || "")) };
    if (b.display_value?.role === "llm_gen") props.display_value = nullable("string", lbl + (b.display_value.prompt || ""));
    if (b.cite) props.value__evidence = nullable("string", `${lbl}${EVIDENCE_DESC}`);
    return { type: "object", properties: props, required: Object.keys(props), additionalProperties: false };
  },
  buildShapeDescription(b) { return `{ "value": <number 0-100|null>, "target": <number|null>, "display_value": "<string>" }`; },
};

const pieKind = {
  name: "pie", label: "Pie Chart",
  extractionPhases: ["llm"],
  isBlockLevelTarget: true, skipFromPinSchema: false, isCodeDerived: false,
  buildDataSchema(b, o) {
    return {
      type: "object",
      properties: {
        segments: {
          type: "array",
          description: "One entry per slice. Derive label and count from the source counts.",
          items: {
            type: "object",
            properties: {
              label: { type: "string", description: "Slice label" },
              count: { type: "number", description: "Numeric count or value for this slice" }
            },
            required: ["label", "count"],
            additionalProperties: false
          }
        }
      },
      required: ["segments"],
      additionalProperties: false
    };
  },
  buildShapeDescription(b) { return `{ "segments": [{ "label": "<string>", "count": <number> }, ...] }`; },
};

const coverageKind = {
  name: "coverage", label: "Coverage Bars",
  extractionPhases: ["deterministic"],
  isBlockLevelTarget: false, skipFromPinSchema: false, isCodeDerived: true,
  buildDataSchema(b, o) {
    return {
      type: "object",
      properties: {
        segments: {
          type: "array",
          items: {
            type: "object",
            properties: {
              label: { type: "string" },
              count: { type: "number" },
              total: { type: "number" }
            },
            required: ["label", "count", "total"],
            additionalProperties: false
          }
        }
      },
      required: ["segments"],
      additionalProperties: false
    };
  },
  buildShapeDescription(b) { return `{ "segments": [{ "label": "<string>", "count": <number>, "total": <number> }, ...] }`; },
};

export const chartDef = {
  type: "chart", label: "Chart",
  description: "Bar, line, heatmap, progress, or pie chart.",

  subTypes: {
    chartKind: (() => {
      const sr = BlockRegistry.createSubRegistry();
      sr.register(barKind); sr.register(lineKind); sr.register(heatmapKind);
      sr.register(progressKind); sr.register(pieKind); sr.register(coverageKind);
      return sr;
    })(),
  },

  crystallizer: {
    extractionPhases(b) {
      const k = chartDef.subTypes.chartKind.getIfRegistered(b.kind || "bar");
      if (!k) return ["llm"];
      return typeof k.extractionPhases === "function" ? k.extractionPhases(b) : k.extractionPhases;
    },

    buildDataSchema(b, o) {
      const k = chartDef.subTypes.chartKind.getIfRegistered(b.kind || "bar");
      return k ? k.buildDataSchema(b, o) : { type: "object", additionalProperties: true };
    },

    buildShapeDescription(b) {
      const k = chartDef.subTypes.chartKind.getIfRegistered(b.kind || "bar");
      return k ? k.buildShapeDescription(b) : "{ ... }";
    },

    isBlockLevelTarget(b) {
      const k = chartDef.subTypes.chartKind.getIfRegistered(b.kind || "bar");
      if (!k) return false;
      return typeof k.isBlockLevelTarget === "function" ? k.isBlockLevelTarget(b) : k.isBlockLevelTarget;
    },

    skipFromPinSchema(b) {
      const k = chartDef.subTypes.chartKind.getIfRegistered(b.kind || "bar");
      if (!k) return false;
      return typeof k.skipFromPinSchema === "function" ? k.skipFromPinSchema(b) : k.skipFromPinSchema;
    },

    isCodeDerived(b) {
      const k = chartDef.subTypes.chartKind.getIfRegistered(b.kind || "bar");
      if (!k) return false;
      return typeof k.isCodeDerived === "function" ? k.isCodeDerived(b) : k.isCodeDerived;
    },

    collectHints() { return []; },
    buildPromptSupplement() { return ""; },
    postProcess(d) { return d; },
  },

  renderer: null,

  editor: {
    icon: "chart-bar", category: "data-visualization",
    defaultTemplate: { type: "chart", kind: "bar", series: [{ key: "value", label: "Value", color: "#1F3A2E" }] },
    configFields: [
      { name: "kind", type: "select", label: "Chart type", options: ["bar","line","heatmap","progress","pie","coverage"], defaultValue: "bar" },
      // `label` is rendered as a visible title only by the heatmap and progress
      // kinds (chart.js renderer). Bar/line/pie/coverage get titles from their
      // enclosing section/band header, not block.label.
      { name: "label", type: "string", label: "Chart title", showIf: { kind: ["heatmap", "progress"] } },
      { name: "proportion", type: "number", label: "Proportion weight", step: 0.1 },
      // Series (array of {key, label, color, label_position, label_format, dash})
      // — consumed only by the bar/line render paths.
      { name: "series", type: "array", label: "Series", showIf: { kind: ["bar", "line"] },
        itemSchema: { fields: [
          { name: "key", type: "string", label: "Data key" },
          { name: "label", type: "string", label: "Display label" },
          { name: "color", type: "color", label: "Color" },
          { name: "label_position", type: "select", label: "Label position", options: ["above_bar", "none"], defaultValue: "none" },
          { name: "label_format", type: "select", label: "Label format", options: ["currency", "currency_m", "currency_gbp", "currency_gbp_m", "pct_1dp", "num_1dp", "num_0dp"], defaultValue: "currency" },
        ]}
      },
      // Y-axis config — gridlines/ticks are drawn only for bar/line.
      { name: "y_axis", type: "object", label: "Y Axis", showIf: { kind: ["bar", "line"] },
        fields: [
          { name: "unit", type: "string", label: "Unit label (e.g. £bn)" },
          { name: "value_divisor", type: "number", label: "Value divisor", defaultValue: 1 },
          { name: "value_decimals", type: "number", label: "Decimal places", defaultValue: 0 },
        ]
      },
      // Source binding — `pins` is the primary, universal binding the
      // crystallizer reads for every block (viz_dsl_crystallizer resolves it
      // first). `data_binding.pin` is only a chart-specific fallback used when
      // `pins` is absent (1 of 33 example charts), so it is NOT surfaced here —
      // exposing `pins` instead matches what actually routes the chart's data.
      // Precedence at crystallize time: block.pins → (charts) data_binding.pin
      // → inherited ancestor pins → `_default`.
      { name: "pins", type: "tags", label: "Source pins",
        help: "Routes this chart to its source data during crystallize. Leave empty to inherit the nearest ancestor's pins. The bars themselves are drawn from series[].key below."
      },
      // Heatmap-only rendering options.
      { name: "heat_color", type: "color", label: "Heatmap max color", showIf: { kind: ["heatmap"] } },
      { name: "cell_height", type: "number", label: "Cell height px", showIf: { kind: ["heatmap"] } },
      // Progress-only rendering options.
      { name: "bar_height", type: "number", label: "Bar height px", showIf: { kind: ["progress"] } },
      { name: "token_fill_pct", type: "number", label: "Token fill %", showIf: { kind: ["progress"] } },
      { name: "null_fallback", type: "string", label: "Null fallback display", showIf: { kind: ["progress"] } },
    ],
  },
};
